import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress } from '@mui/material';

import WizardDataContext from '../store/WizardDataContext';
import { fetchPopinById, renewPopinConfirm } from '../utils/wizardApi';
import { POPIN_TOKEN_INVALID_MESSAGE } from '../utils/couponApiUtils';

const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function RenewPopin() {
  const { popinId } = useParams();
  const [searchParams] = useSearchParams();
  const manageToken = searchParams.get('t') || '';

  const navigate = useNavigate();
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popinEnvelope, setPopinEnvelope] = useState(null);

  const [selectedRate, setSelectedRate] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  const [popinRateOptions, setPopinRateOptions] = useState([]);
  const [popinRatesLoading, setPopinRatesLoading] = useState(false);
  const [popinRatesError, setPopinRatesError] = useState(null);

  const affiliateCode = wizardData?.meta?.affiliateCode || null;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!popinId || !manageToken) {
        setError('Missing renewal token.');
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await fetchPopinById({ popinId, manageToken });
      if (cancelled) return;

      if (!res.success) {
        if (res.tokenInvalid) setError(res.message || POPIN_TOKEN_INVALID_MESSAGE);
        else setError(res.error || 'Failed to load pop-in');
        setLoading(false);
        return;
      }

      setPopinEnvelope(res.data);
      setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [popinId, manageToken]);

  useEffect(() => {
    const controller = new AbortController();
    setPopinRatesLoading(true);
    setPopinRatesError(null);

    fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/popin/rates/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: controller.signal
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === 'success' && Array.isArray(data?.data)) {
          setPopinRateOptions(data.data);
        } else {
          setPopinRateOptions([]);
          setPopinRatesError(data?.message || 'Failed to load Pop-In rates');
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setPopinRateOptions([]);
        setPopinRatesError('Failed to load Pop-In rates');
      })
      .finally(() => {
        setPopinRatesLoading(false);
      });

    return () => controller.abort();
  }, []);

  const derived = useMemo(() => {
    const d = popinEnvelope;
    if (!d) return null;
    const advertising = d.advertising || {};
    const coupon = d.coupon || null;

    const companyName =
      advertising.company_name ||
      advertising.companyName ||
      advertising.name ||
      d.company_name ||
      d.companyName ||
      'Company';

    const customerEmail = advertising.email_address || advertising.email || d.customer_email || '';
    const customerName = advertising.contact_person || advertising.contact || d.customer_name || advertising.company_name || '';

    const status = d.status ?? null;

    const couponHasLocations = Array.isArray(coupon?.locations) ? coupon.locations.length : null;
    const couponHasCategories = Array.isArray(coupon?.categories) ? coupon.categories.length : null;
    const couponHasCountries = Array.isArray(coupon?.countries) ? coupon.countries.length : null;

    return {
      advertising,
      coupon,
      companyName,
      customerEmail,
      customerName,
      status,
      couponCounts: {
        locations: couponHasLocations,
        categories: couponHasCategories,
        countries: couponHasCountries
      }
    };
  }, [popinEnvelope]);

  const handleEdit = () => {
    if (!derived) return;

    const d = popinEnvelope;
    const advertising = derived.advertising;
    const coupon = derived.coupon;
    const pid = toNumberOrNull(popinId);
    const companyId =
      toNumberOrNull(advertising.id) ||
      toNumberOrNull(advertising.advertising_id) ||
      toNumberOrNull(advertising.company_id) ||
      toNumberOrNull(d.advertising_id) ||
      null;

    const couponId = coupon ? (toNumberOrNull(coupon.id) || toNumberOrNull(coupon.coupon_id)) : null;
    const includeCoupon = !!couponId;

    // Hydrate minimal wizard domains so the existing edit UI can render something sensible.
    updateData('meta', {
      companyId,
      couponId,
      popinId: pid,
      popinManageToken: manageToken
    });
    updateData('dealSelection', { selectedDeal: 'popin', includeCoupon });

    updateData('company', {
      name: advertising.company_name || advertising.name || '',
      email: advertising.email_address || advertising.email || '',
      managerName: advertising.contact_person || advertising.contact || '',
      managerNumber: advertising.phone_number || advertising.mobile || '',
      website: advertising.website || 'https://',
      description: advertising.description || '',
      backgroundColor: advertising.background_color || advertising.backgroundColor || '#FFFFFF',
      companyId
    });

    updateData('locations', {
      list: [],
      isOnlineOnly: false,
      countries: [],
      isGlobal: false
    });

    updateData('popinBudget', {
      visitors: d.visitors ?? null,
      price: d.price ?? null,
      popinRateId: d.popin_rate_id ?? d.popinRateId ?? null
    });

    const returnTo = `/renew/${popinId}?t=${manageToken}`;
    const qs = new URLSearchParams();
    if (companyId != null) qs.set('companyId', String(companyId));
    if (couponId != null) qs.set('couponId', String(couponId));
    if (pid != null) qs.set('popinId', String(pid));
    qs.set('returnTo', returnTo);

    // Open the existing Resume page (sectioned editor entry point).
    navigate(`/advertise/coupon/resume?${qs.toString()}`);
  };

  const handlePay = async () => {
    const claims = Number(selectedRate?.visitors);
    if (!selectedRate || !Number.isFinite(claims) || claims <= 0) {
      setError('Please select a Pop-In option.');
      return;
    }
    if (!derived?.customerEmail) {
      setError('Customer email is missing.');
      return;
    }
    if (!manageToken) {
      setError('Missing renewal token.');
      return;
    }

    setError(null);
    setIsPaying(true);
    try {
      const res = await renewPopinConfirm({
        popinId,
        manageToken,
        additionalClaims: claims,
        popinRateId: selectedRate.id,
        customerEmail: derived.customerEmail,
        customerName: derived.customerName || undefined,
        affiliateCode: affiliateCode || undefined
      });

      if (!res.success) {
        if (res.tokenInvalid) setError(res.message || POPIN_TOKEN_INVALID_MESSAGE);
        else setError(res.error || 'Failed to create payment link');
        setIsPaying(false);
        return;
      }

      const paymentUrl = res.data?.paymentUrl || res.data?.url || null;
      if (paymentUrl) {
        window.location.href = paymentUrl;
        return;
      }

      setError('Payment link was not returned.');
      setIsPaying(false);
    } catch (e) {
      setError(e.message || 'Failed to create payment link');
      setIsPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-processing-container">
        <div className="payment-processing-content">
          <CircularProgress size={60} style={{ color: 'white', marginBottom: '20px' }} />
          <h1>Loading renewal</h1>
          <p>Please wait while we load your PopIn deal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-error-container">
        <div className="payment-error-content">
          <h1 className="payment-error-title">Oops</h1>
          <p className="payment-error-message">{error}</p>
          <div className="payment-error-actions">
            <button onClick={() => navigate('/')} className="payment-error-btn-secondary">
              GO TO HOME
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!derived) return null;

  const hasCoupon = !!derived.coupon;

  return (
    <div className="resume-container renew-popin-page">
      <div className="resume-inner">
        <div className="company-summary" style={{ backgroundColor: derived.advertising.background_color || '#FFFFFF' }}>
          <div className="resume-header-text" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h1 style={{ margin: 0 }}>Renew your PopIn deal</h1>
            <p style={{ margin: '8px 0 0', opacity: 0.8 }}>Edit details if needed, then choose how many claims to add.</p>
          </div>

          <div className="company-name">
            <h1>{derived.companyName}</h1>
          </div>

          <div className="company-details" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div>
              {derived.advertising.contact_person && (
                <div className="detail-item">
                  <div className="detail-label">Contact</div>
                  <div className="detail-value">{derived.advertising.contact_person}</div>
                </div>
              )}
              {derived.customerEmail && (
                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{derived.customerEmail}</div>
                </div>
              )}
            </div>
            <div>
              {derived.advertising.website && (
                <div className="detail-item">
                  <div className="detail-label">Website</div>
                  <div className="detail-value">{derived.advertising.website}</div>
                </div>
              )}
              {derived.status && (
                <div className="detail-item">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">{String(derived.status)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Edit PopIn deal (temporarily disabled) */}
          {/*
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button variant="contained" onClick={handleEdit}>
                Edit PopIn deal
              </Button>
            </Box>
          */}
        </div>
      </div>

      <div className="coupon-coupon-container-2">
        <div className="resume-inner">
          {hasCoupon && (
            <Box sx={{ mt: 2 }}>
              <div className="main-title full-width">
                <h1>Bundled coupon</h1>
              </div>
              <div className="company-details" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="detail-item">
                  <div className="detail-label">Coupon locations</div>
                  <div className="detail-value">{derived.couponCounts.locations ?? '—'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Categories</div>
                  <div className="detail-value">{derived.couponCounts.categories ?? '—'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Countries</div>
                  <div className="detail-value">{derived.couponCounts.countries ?? '—'}</div>
                </div>
              </div>
            </Box>
          )}

          <div className="step4-container" style={{ marginTop: '28px' }}>
            <div className="step4-header">
              <h1>Select Pay-Per-Visit Budget</h1>
              <h3>Choose the visitor target and budget for your Pop-In renewal</h3>
            </div>

            <div className="expiration-grid">
              <div className="expiration-group">
                <div className="expiration-group__grid" role="radiogroup" aria-label="Pop-In budget options">
                  {popinRatesLoading && (
                    <p className="expiration-grid__loading">Loading Pop-In budget options...</p>
                  )}
                  {!popinRatesLoading && popinRatesError && (
                    <p className="expiration-grid__empty">{popinRatesError}</p>
                  )}
                  {!popinRatesLoading &&
                    !popinRatesError &&
                    popinRateOptions.map((option) => {
                      const isSelected = selectedRate != null && selectedRate.id === option.id;

                      return (
                        <button
                          key={option.id != null ? option.id : `${option.visitors}-${option.price}`}
                          type="button"
                          className={`expiration-card${isSelected ? ' expiration-card--selected' : ''}`}
                          onClick={() => setSelectedRate(option)}
                          role="radio"
                          aria-checked={isSelected}
                        >
                          <div className="expiration-card__duration">{option.visitors} Visitors</div>
                          <div className="expiration-card__price">R{option.price}</div>
                        </button>
                      );
                    })}
                </div>
              </div>

              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '18px' }}>
                <Button variant="contained" onClick={handlePay} disabled={isPaying || !selectedRate}>
                  {isPaying ? 'Creating payment…' : 'Pay'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

